/**
 * One place that turns an address into a Google Maps link.
 *
 * Every address on the platform should open the map at that exact place, and
 * a link needs no API key — the Maps URL scheme is public. Coordinates win
 * when the deal has them, because a geocoded lat/lng lands on the building
 * while a text query lands on whatever Google thinks the words mean.
 *
 * https://developers.google.com/maps/documentation/urls/get-started
 */

export type MapsTarget = {
  address?: string | null
  lat?: number | null
  lng?: number | null
}

const MAPS_SEARCH = 'https://www.google.com/maps/search/?api=1'

function usableCoords(lat: unknown, lng: unknown): [number, number] | null {
  const a = Number(lat)
  const b = Number(lng)
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  // 0,0 is in the Gulf of Guinea. It is never a property, and it is what an
  // un-geocoded record looks like once null becomes a number.
  if (a === 0 && b === 0) return null
  if (Math.abs(a) > 90 || Math.abs(b) > 180) return null
  return [a, b]
}

/** The Google Maps URL for a place, or null when there is nothing to point at. */
export function mapsUrl({ address, lat, lng }: MapsTarget): string | null {
  const coords = usableCoords(lat, lng)
  const text = typeof address === 'string' ? address.trim() : ''

  if (coords) return `${MAPS_SEARCH}&query=${encodeURIComponent(`${coords[0]},${coords[1]}`)}`
  if (!text) return null
  return `${MAPS_SEARCH}&query=${encodeURIComponent(text)}`
}

/** True when there is enough to open a map for. */
export function hasMapTarget(target: MapsTarget): boolean {
  return mapsUrl(target) !== null
}
